import { type MapStateToPropsFactory } from "react-redux";

import { type RadioListOption, type SegmentedControlOptions } from "@ppb/the-wall-common/types";

import {
  type BeaconsDisplayPreferenceClickAction,
  type PreferenceClickAction,
  UI__BEACONS_DISPLAY_PREFERENCE_CLICK,
  UI__PREFERENCE_CLICK,
} from "@ppb/tbd-store/actions/preferences";
import { type ApplicationState } from "@ppb/tbd-store/state/ApplicationState.types";
import { Jurisdiction } from "@ppb/tbd-store/state/constants";
import URN from "@ppb/tbd-store/state/layout/URN";
import {
  PreferenceLayout,
  PreferenceSingleChoice,
  SpecialSingleChoicePreferences,
} from "@ppb/tbd-store/state/entities/settings-preferences/SettingsPreferences.types";
import { type PreferenceSingleChoiceCards } from "@ppb/tbd-store/state/layout/cards/Card.types";
import { type UserDetails } from "@ppb/tbd-store/state/entities/user-details/UserDetailsState";
import { createCardByURNSelector } from "@ppb/tbd-store/state/layout/cards/cards-selectors";
import { createSettingsPreferenceSelector } from "@ppb/tbd-store/state/entities/settings-preferences/settings-preferences-selectors";
import { getUserDetails } from "@ppb/tbd-store/state/entities/user-details/user-details-selectors";
import { getCanUsePhoenixExchange } from "@ppb/tbd-store/state/boot/boot-selectors";
import { createGetThrottleSelector } from "@ppb/tbd-store/state/entities/throttles/throttles-selectors";

import { TranslationKey } from "../../translations/keys";
import { i18n } from "../../helpers/i18n";
import { getExternalLink } from "../../helpers/external-links";

export type ContainerProps = {
  urn: URN;
};

export type CardProps = {
  preferenceUrn: string;
  title?: string;
  hint?: string;
  selectedOptionIndex: number;
  listOptions: RadioListOption[];
  segmentedOptions?: SegmentedControlOptions[];
  isVisible: boolean;
  isSwitchLayout: boolean;
  isLoggedIn: boolean;
  layout?: string;
  learnMoreUrl?: string;
  learnMoreLabel?: string;
};

export type StateProps = CardProps | Record<string, never>;

const getListOptions = (
  { selectedValueIndex, preferenceValues }: PreferenceSingleChoice,
  isSwitchLayout: boolean,
): Pick<CardProps, "selectedOptionIndex" | "listOptions"> => {
  let selectedOptionIndex = selectedValueIndex;
  const listOptions = preferenceValues.map(({ translationKey, value }) => ({
    id: value,
    text: i18n({ key: translationKey as keyof TranslationKey }),
  }));

  /*
   * Switch preference layout uses "selectedOptionIndex" as boolean for switch state.
   * Preference option may come as "0 - ON" / "1 - OFF", which inverts the Boolean logic.
   * Reversing list options to "0 - OFF" / "1 - ON" matches Boolean logic
   */
  const shouldReverseOptions = listOptions.length && listOptions[0].id === "ON";
  if (isSwitchLayout && shouldReverseOptions) {
    selectedOptionIndex = Number(!selectedOptionIndex);
    listOptions.reverse();
  }

  return { selectedOptionIndex, listOptions };
};

export const makeMapStateToProps: MapStateToPropsFactory<StateProps, ContainerProps, ApplicationState> = () => {
  const getPreferenceSingleChoiceCards = createCardByURNSelector<PreferenceSingleChoiceCards, URN>();
  const getSettingsPreference = createSettingsPreferenceSelector();
  const getThrottle = createGetThrottleSelector();

  return function mapStateToProps(state: ApplicationState, { urn }: ContainerProps): StateProps {
    try {
      const {
        entities: { settingspreferences, userdetails },
        layouts,
      } = state;

      const preferenceSingleChoiceCard = getPreferenceSingleChoiceCards(layouts.cards.preferencesinglechoices, urn);
      if (!preferenceSingleChoiceCard) return {};

      const preference = getSettingsPreference(settingspreferences, preferenceSingleChoiceCard.preferenceURN);
      if (!preference) return {};

      const { preferenceKey, preferenceValues } = preference as PreferenceSingleChoice;

      const isConfirmCashoutPreference = preferenceKey === SpecialSingleChoicePreferences.confirmCashout;
      const {
        jurisdiction: { jurisdiction },
      } = <UserDetails>getUserDetails(state);
      const isBrazilJurisdiction = jurisdiction === Jurisdiction.BRAZIL;
      const isConfirmCashoutThrottleActive = getThrottle(state.entities.throttles, "CONFIRM_CASHOUT_PREFERENCE")
        ?.isActive;
      if (isConfirmCashoutPreference && (!isConfirmCashoutThrottleActive || isBrazilJurisdiction)) return {};

      const isUserProductsPref = preferenceKey === SpecialSingleChoicePreferences.defaultProduct;

      // The Default Product card is only meaningful for users eligible for Phoenix Exchange.
      const isVisible = !isUserProductsPref || getCanUsePhoenixExchange(state);

      const title = i18n({ key: preferenceSingleChoiceCard.title as keyof TranslationKey });
      const description = i18n({ key: preferenceSingleChoiceCard.description as keyof TranslationKey });

      let segments;
      if (preferenceSingleChoiceCard.layout === PreferenceLayout.Segmented) {
        segments = preferenceValues.map((pref) => ({
          key: pref.value,
          value: i18n({ key: pref.translationKey as keyof TranslationKey }),
        }));
      }

      const isBeaconsDisplayPreference = preferenceKey === SpecialSingleChoicePreferences.exchangeBeaconsDisplay;

      const isSwitchLayout = isConfirmCashoutPreference || isBeaconsDisplayPreference;

      const { selectedOptionIndex, listOptions } = getListOptions(preference as PreferenceSingleChoice, isSwitchLayout);

      return {
        preferenceUrn: preference.urn,
        title: preferenceSingleChoiceCard.title !== title ? title : undefined,
        hint: preferenceSingleChoiceCard.description !== description ? description : undefined,
        selectedOptionIndex,
        listOptions,
        isVisible,
        isSwitchLayout,
        isLoggedIn: userdetails.loggedIn,
        layout: preferenceSingleChoiceCard.layout,
        segmentedOptions: segments,
        learnMoreUrl: isBeaconsDisplayPreference ? getExternalLink("BEACONS_LEARN_MORE") : undefined,
        learnMoreLabel: isBeaconsDisplayPreference
          ? i18n({ key: "I18N.PREFERENCES.BEACONS_DISPLAY.SECONDARY_LABEL" })
          : undefined,
      };
    } catch (e) {
      console.error(e);

      return {};
    }
  };
};

const dispatchPreferenceChange = (
  urn: URN,
  value: string,
): PreferenceClickAction | BeaconsDisplayPreferenceClickAction => ({
  type: urn.includes(SpecialSingleChoicePreferences.exchangeBeaconsDisplay)
    ? UI__BEACONS_DISPLAY_PREFERENCE_CLICK
    : UI__PREFERENCE_CLICK,
  payload: { urn, value },
});

export type DispatchProps = {
  dispatchPreferenceChange: typeof dispatchPreferenceChange;
};

export const mapDispatchToProps: DispatchProps = { dispatchPreferenceChange };
